import { Link } from '@tanstack/react-router'
import { ArrowRight, Loader2, type LucideIcon } from 'lucide-react'

import { asRoute, asSearch } from '@/utils/runtime-route'

/** Ground, label and the ink shared by icon and spinner. One entry per tone so
 *  a colour is never picked at a call site. */
const CTA_TONES = {
  solid: {
    ground: 'bg-neutral-900 hover:bg-neutral-800',
    label: 'text-white',
  },
  outline: {
    ground: 'border border-neutral-200 hover:bg-neutral-50',
    label: 'text-neutral-600',
  },
  /** Outline without the rule — for a secondary action that sits beside a
   *  solid one and must not compete with it. */
  ghost: {
    ground: 'hover:bg-neutral-50',
    label: 'text-neutral-600',
  },
  /** An action that destroys something. */
  danger: {
    ground: 'bg-destructive hover:bg-destructive/90',
    label: 'text-white',
  },
  /** The warm brand accent, for the one action a page is asking for. */
  accent: {
    ground: 'bg-orange-600 hover:bg-orange-700',
    label: 'text-white',
  },
  /** On a dark ground, e.g. inside the public price band. */
  inverted: {
    ground: 'bg-white hover:bg-neutral-200',
    label: 'text-neutral-900',
  },
} as const

/** Padding and type together, so a smaller button is not also a thinner one. */
const SIZES = {
  sm: { box: 'px-4 py-2.5', label: 'text-xs', glyph: 14 },
  md: { box: 'px-6 py-3.5', label: 'text-sm', glyph: 16 },
  /** The one action a whole section exists to offer. */
  lg: { box: 'px-10 py-5', label: 'text-base tracking-wide', glyph: 18 },
} as const

const LAYOUTS = {
  /** Only as wide as its label. */
  hug: 'self-start',
  /** Fills its parent's width. */
  full: 'w-full',
  /** Shares a row equally with its siblings. */
  fill: 'flex-1',
  /** Gives way when a row runs out of room; the label truncates. */
  shrink: 'shrink min-w-0',
} as const

type CtaButtonProps = {
  label: string
  /** Trailing arrow by default; `null` renders no icon. */
  icon?: LucideIcon | null
  iconPosition?: 'leading' | 'trailing'
  tone?: keyof typeof CTA_TONES
  size?: keyof typeof SIZES
  /** Replaces the icon with a spinner in the tone's ink. */
  loading?: boolean
  disabled?: boolean
  /** At most one of these three. */
  onClick?: () => void
  /** Submits the `<form>` the button sits inside. */
  submits?: boolean
  /** Submits the `<form>` with this id. A dialog pins its footer outside the
   *  scrolling body, so the button that submits a form sits outside it. */
  submitsForm?: string
  to?: string
  /** Query the `to` route carries — a `to` string cannot hold one. */
  search?: Record<string, string>
  /** Off-site target. Opens in a new tab. */
  externalHref?: string
  /** Renders a span instead of a button: the card around it is the link and
   *  must receive the press. Stated rather than inferred from a missing
   *  handler, so a button that lost its `onClick` stays dead rather than
   *  silently becoming decoration. */
  decorative?: boolean
  /** Where the button sits in its parent. A closed set, because padding,
   *  radius and type are fixed — every call site shares one shape. */
  layout?: keyof typeof LAYOUTS
}

/**
 * The call-to-action button of the Customer and Public areas: one shape, four
 * tones. Cross-area because a visitor moves from the public configurator into
 * their own project and must not see the same action change size on the way.
 *
 * Internal has its own navy `@/components/Internal/Button` — this one carries
 * the black editorial aesthetic and does not belong on Internal screens.
 */
export function CtaButton({
  label,
  icon = ArrowRight,
  iconPosition = 'trailing',
  tone = 'solid',
  size = 'md',
  loading = false,
  disabled = false,
  onClick,
  submits = false,
  submitsForm,
  to,
  search,
  externalHref,
  decorative = false,
  layout,
}: CtaButtonProps) {
  const { ground, label: labelClass } = CTA_TONES[tone]
  // A press while the last one is still in flight sends the request twice —
  // a second login, a second reset mail, a second consent grant. The spinner
  // says it is working; this is what stops it being pressed again.
  const refused = disabled || loading
  const { box, label: labelSize, glyph: glyphSize } = SIZES[size]
  const Icon = icon

  const glyph = loading ? (
    <Loader2 size={glyphSize} className="animate-spin" aria-hidden />
  ) : Icon ? (
    <Icon size={glyphSize} aria-hidden />
  ) : null

  const content = (
    <>
      {iconPosition === 'leading' && glyph}
      <span className={`truncate font-semibold ${labelSize}`}>{label}</span>
      {iconPosition === 'trailing' && glyph}
    </>
  )

  const shape = `inline-flex flex-row items-center justify-center gap-2 rounded-lg transition-colors ${box} ${ground} ${labelClass} ${
    disabled ? 'opacity-50' : ''
  } ${layout ? LAYOUTS[layout] : ''}`

  if (decorative) {
    return <span className={shape}>{content}</span>
  }

  if (externalHref) {
    return (
      <a
        href={externalHref}
        target="_blank"
        rel="noreferrer noopener"
        className={shape}
      >
        {content}
      </a>
    )
  }

  if (to) {
    return (
      <Link to={asRoute(to)} search={asSearch(search)} className={shape}>
        {content}
      </Link>
    )
  }

  return (
    <button
      type={submits || submitsForm ? 'submit' : 'button'}
      form={submitsForm}
      onClick={onClick}
      disabled={refused}
      className={shape}
    >
      {content}
    </button>
  )
}
